import type { Author, Cast, Person } from '@/types'

/** Role used for authors in title-contributions (migrated from legacy authors). */
export const AUTHOR_ROLE = 'author'

/** Role used for cast in title-contributions (migrated from legacy casts). */
export const CAST_ROLE = 'starring'

export const TITLE_CONTRIBUTOR_INCLUDES = [
  'people',
  'contributions',
  'contributions.person',
] as const

export const TITLE_RELATION_INCLUDES = [
  'people',
  'contributions',
  'contributions.person',
  'categories',
  'series',
  'channel',
  'tags',
  'rating',
  'content-type',
] as const

export const TITLE_DETAIL_INCLUDES = [
  ...TITLE_RELATION_INCLUDES,
  'chapters',
  'recommendations',
  'recommendations.chapters',
] as const

interface PersonLike {
  id: string | number
  type?: string
  name: string
  surname?: string
  slug?: string
  about?: string
  website?: string
  image?: string | unknown
  titlesPublished?: number
  role?: string
  order?: number | null
}

interface ContributionLike {
  role?: string
  order?: number | null
  personId?: string | number | null
  person?: PersonLike | { id: string | number; type?: string } | null
}

export interface TitleWithContributors {
  type?: string
  authors?: Author[]
  casts?: Cast[]
  people?: PersonLike[]
  contributions?: ContributionLike[]
  recommendations?: TitleWithContributors[]
}

export interface TitleAttributionGroup {
  role: string
  /** Browse filter type for UITitleFilterLinks */
  filterType: 'author' | 'cast'
  people: PersonLike[]
}

function resolvePerson(
  contribution: ContributionLike,
  peopleById: Map<string, PersonLike>
): PersonLike | null {
  const person = contribution.person
  if (person && 'name' in person && person.name) {
    return person
  }

  const personId =
    (person && 'id' in person && person.id != null ? person.id : null) ??
    contribution.personId ??
    null

  if (personId != null) {
    return peopleById.get(String(personId)) ?? null
  }

  return null
}

function hasPeopleData(title: TitleWithContributors): boolean {
  return Boolean(title.people?.length || title.contributions?.length)
}

/**
 * Build a person lookup from title.people and any nested contribution.person
 * payloads (needed when contributions.person is included but people is sparse).
 */
function buildPeopleById(title: TitleWithContributors): Map<string, PersonLike> {
  const peopleById = new Map<string, PersonLike>()

  for (const person of title.people ?? []) {
    if (person?.id != null && person.name) {
      peopleById.set(String(person.id), person)
    }
  }

  for (const contribution of title.contributions ?? []) {
    const person = contribution.person
    if (person && 'name' in person && person.name && person.id != null) {
      peopleById.set(String(person.id), person)
    }
  }

  return peopleById
}

function embeddedContributions(person: PersonLike): ContributionLike[] {
  const nested = (person as PersonLike & { contributions?: ContributionLike[] }).contributions
  return Array.isArray(nested) ? nested : []
}

function peopleByRole(title: TitleWithContributors, role: string): PersonLike[] {
  if (!hasPeopleData(title)) {
    return []
  }

  const peopleById = buildPeopleById(title)
  const contributions = title.contributions ?? []

  const fromContributions = contributions
    .filter(contribution => contribution.role === role)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(contribution => resolvePerson(contribution, peopleById))
    .filter((person): person is PersonLike => person != null)

  if (fromContributions.length) {
    return fromContributions
  }

  const people = title.people ?? []
  const withEmbedded = people.filter(person =>
    embeddedContributions(person).some(contribution => contribution.role === role)
  )
  if (withEmbedded.length) {
    return withEmbedded.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  }

  return people
    .filter(person => person.role === role)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

function collectRoles(title: TitleWithContributors): string[] {
  const roles = new Set<string>()

  for (const contribution of title.contributions ?? []) {
    if (contribution.role) {
      roles.add(contribution.role)
    }
  }

  for (const person of title.people ?? []) {
    if (person.role) {
      roles.add(person.role)
    }
    for (const contribution of embeddedContributions(person)) {
      if (contribution.role) {
        roles.add(contribution.role)
      }
    }
  }

  return [...roles]
}

function sortRoles(roles: string[]): string[] {
  // Author first when present; every other role is dynamic and alphabetical
  return [...roles].sort((a, b) => {
    if (a === AUTHOR_ROLE && b !== AUTHOR_ROLE) return -1
    if (b === AUTHOR_ROLE && a !== AUTHOR_ROLE) return 1
    return a.localeCompare(b)
  })
}

/** Humanize an API contribution role for meta labels (fully dynamic). */
export function formatAttributionRoleLabel(role: string): string {
  const normalized = role.trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ')
  if (!normalized) return 'Contributor'
  return normalized.replace(/\b\w/g, char => char.toUpperCase())
}

export function personToAuthor(person: PersonLike): Author {
  return {
    id: String(person.id),
    type: 'authors',
    title: `${person.name} ${person.surname ?? ''}`.trim(),
    name: person.name,
    surname: person.surname ?? '',
    about: person.about,
    website: person.website,
    slug: person.slug,
    image: typeof person.image === 'string' ? person.image : undefined,
    titlesPublished: person.titlesPublished ?? 0,
  }
}

export function personToCast(person: PersonLike): Cast {
  return {
    id: String(person.id),
    type: 'casts',
    title: `${person.name} ${person.surname ?? ''}`.trim(),
    name: person.name,
    surname: person.surname ?? '',
    slug: person.slug,
    titlesPublished: person.titlesPublished ?? 0,
  }
}

export function personToFilterItem(person: PersonLike): Person {
  return {
    id: String(person.id),
    type: 'people',
    title: `${person.name} ${person.surname ?? ''}`.trim(),
    name: person.name,
    surname: person.surname ?? '',
    slug: person.slug,
    about: person.about,
    website: person.website,
    titlesPublished: person.titlesPublished ?? 0,
    image: typeof person.image === 'string' ? person.image : undefined,
  }
}

export function resolveTitleAuthors(title: TitleWithContributors): Author[] {
  const fromPeople = peopleByRole(title, AUTHOR_ROLE).map(personToAuthor)
  if (fromPeople.length) {
    return fromPeople
  }

  return title.authors ?? []
}

export function resolveTitleCasts(title: TitleWithContributors): Cast[] {
  const fromPeople = peopleByRole(title, CAST_ROLE).map(personToCast)
  if (fromPeople.length) {
    return fromPeople
  }

  return title.casts ?? []
}

export function resolveTitleNarrators(title: TitleWithContributors): PersonLike[] {
  return peopleByRole(title, 'narrator')
}

/**
 * All attribution groups for title meta (people preferred).
 * Roles come dynamically from contributions/people — only author is ordered first.
 */
export function groupTitleAttributions(title: TitleWithContributors): TitleAttributionGroup[] {
  const groups: TitleAttributionGroup[] = []
  const roles = sortRoles(collectRoles(title))

  for (const role of roles) {
    const people = peopleByRole(title, role)
    if (!people.length) continue

    groups.push({
      role,
      filterType: role === AUTHOR_ROLE ? 'author' : 'cast',
      people,
    })
  }

  // Legacy fallbacks when people/contributions are absent
  if (!groups.some(group => group.role === AUTHOR_ROLE) && title.authors?.length) {
    groups.push({
      role: AUTHOR_ROLE,
      filterType: 'author',
      people: title.authors.map(author => ({
        id: author.id,
        name: author.name || author.title || '',
        surname: author.surname,
        slug: author.slug,
        titlesPublished: author.titlesPublished,
      })),
    })
  }

  if (!groups.some(group => group.role === CAST_ROLE) && title.casts?.length) {
    groups.push({
      role: CAST_ROLE,
      filterType: 'cast',
      people: title.casts.map(cast => ({
        id: cast.id,
        name: cast.name || cast.title || '',
        surname: cast.surname,
        slug: cast.slug,
        titlesPublished: cast.titlesPublished,
      })),
    })
  }

  return sortRoles(groups.map(group => group.role))
    .map(role => groups.find(group => group.role === role)!)
    .filter(Boolean)
}

export function normalizeTitleContributors<T extends TitleWithContributors>(title: T): T {
  if (!title || title.type !== 'titles') {
    return title
  }

  const normalized: TitleWithContributors = { ...title }

  // Prefer people/contributions over legacy authors/casts when available
  const authors = resolveTitleAuthors(normalized)
  if (authors.length) {
    normalized.authors = authors
  }

  const casts = resolveTitleCasts(normalized)
  if (casts.length) {
    normalized.casts = casts
  }

  if (normalized.recommendations?.length) {
    normalized.recommendations = normalized.recommendations.map(recommendation =>
      normalizeTitleContributors(recommendation)
    )
  }

  return normalized as T
}
